import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { GetClinicCategoriesTemplate } from 'apollo/templates/organization';
import { GetReportsTemplate, GetReportDescriptionTemplate, CreateReportTemplate } from 'apollo/templates/report';
import { getChildClinicsNamesWithParentTemplate } from 'apollo/templates/organization/query.graphql';

type Report = {
    _id: string;
    name: string;
    type: string | null;
    filters: {
        date: boolean;
        clinic: boolean;
        category: boolean;
        status: boolean;
    };
};

type Clinic = {
    _id: string;
    name: string;
};

type Category = {
    _id: string;
    name: string;
    actionSet: {
        _id: string;
        title: string;
    };
};

type ReportsContextType = {
    reports: Report[];
    loading: boolean;
    selectedReport: Report | null;
    setSelectedReport: (report: Report | null) => void;
    reportData: any;
    clinics: Clinic[];
    clinicsLoading: boolean;
    categories: Category[];
    categoriesLoading: boolean;
    getReportDescription: (variables: { language: string; reportName: string }) => void;
    descriptionData: any;
    descriptionLoading: boolean;
    createReport: (variables: { id: string; options?: any }) => Promise<void>;
};

const ReportsContext = createContext<ReportsContextType | undefined>(undefined);

export const ReportsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { data: reportsData, loading } = useQuery(GetReportsTemplate);
    const { data: clinicsData, loading: clinicsLoading } = useQuery(getChildClinicsNamesWithParentTemplate);
    const { data: categoriesData, loading: categoriesLoading } = useQuery(GetClinicCategoriesTemplate);

    const [getReportDescriptionQuery, { data: descriptionData, loading: descriptionLoading }] =
        useLazyQuery(GetReportDescriptionTemplate);

    const [createReportQuery] = useLazyQuery(CreateReportTemplate);

    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [reportData, setReportData] = useState<any>(null);

    const createReport = async (variables: { id: string; options?: any }) => {
        try {
            const response = await createReportQuery({ variables });
            setReportData(response?.data?.createReports ?? null);
        } catch (error) {
            console.error('Error creating report:', error);
        }
    };

    const getReportDescription = (variables: { language: string; reportName: string }) => {
        getReportDescriptionQuery({ variables });
    };

    return (
        <ReportsContext.Provider
            value={{
                reports: reportsData?.reports ?? [],
                loading,
                selectedReport,
                setSelectedReport,
                reportData,
                clinics: clinicsData?.childClinicsWithParent ?? [],
                clinicsLoading,
                categories: categoriesData?.getClinicCategories ?? [],
                categoriesLoading,
                getReportDescription,
                descriptionData,
                descriptionLoading,
                createReport,
            }}
        >
            {children}
        </ReportsContext.Provider>
    );
};

export const useReports = () => {
    const context = useContext(ReportsContext);
    if (!context) {
        throw new Error('useReports must be used within ReportsProvider');
    }
    return context;
};
